// Per-FieldNode-kind dispatch. Per spec/tech-arch.md §7. The form
// wrapper hands each FieldNode to `FieldRenderer`; the table below
// picks the matching field component. Per-kind narrowing is enforced
// by the `FieldRenderers` mapped type.
//
// **`union` rendering — last variant inline** per D-033's paired-
// optional CTA UX. The 5 V1 inputsSchemas + 4 V1 array-element schemas
// have a last "fat" arm whose fields match the rendered surface;
// safeParse picks the matching variant at runtime. Variants with
// non-object kinds fall through to a placeholder.

import type { ComponentChild } from 'preact'

import { getImageTarget } from '../../lib/image-dimensions'
import type { ArrayOverrides } from './form'
import type { FieldNode } from './schema-to-fields'
import { TextField } from './fields/text-field'
import { UrlField } from './fields/url-field'
import { ImageUrlField } from './fields/image-url-field'
import { SelectField } from './fields/select-field'
import { ListField } from './fields/list-field'
import { NestedField } from './fields/nested-field'
import { LazyRichTextField } from './fields/richtext-field-lazy'

export interface FieldRendererProps {
  readonly node: FieldNode
  readonly value: unknown
  readonly path: string
  readonly idPrefix: string
  readonly errors: Readonly<Record<string, string>>
  readonly touched: ReadonlySet<string>
  readonly markTouched: (path: string) => void
  readonly onChange: (next: unknown) => void
  readonly arrayOverrides?: ArrayOverrides
  // Registered Block ID; composed with `path` to look up the per-field
  // resize target. See `Form` for the threading rationale.
  readonly blockId?: string
}

export function FieldRenderer(props: FieldRendererProps): ComponentChild {
  const renderer = FIELD_RENDERERS[props.node.kind] as (p: FieldRendererProps) => ComponentChild
  return renderer(props)
}

type RendererFn<K extends FieldNode['kind']> = (props: {
  readonly node: Extract<FieldNode, { kind: K }>
  readonly value: unknown
  readonly path: string
  readonly idPrefix: string
  readonly errors: Readonly<Record<string, string>>
  readonly touched: ReadonlySet<string>
  readonly markTouched: (path: string) => void
  readonly onChange: (next: unknown) => void
  readonly arrayOverrides?: ArrayOverrides
  readonly blockId?: string
}) => ComponentChild

type FieldRenderers = {
  readonly [K in FieldNode['kind']]: RendererFn<K>
}

interface LeafProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly value: string
  readonly onChange: (next: string) => void
  readonly onBlur: () => void
  readonly required: boolean
  readonly error?: string
}

// Common prop shape for every <input>-driven leaf component (Text /
// Url / ImageUrl / Select). The conditional `error` spread satisfies
// D-006 `exactOptionalPropertyTypes`.
function leafProps(p: FieldRendererProps): LeafProps {
  const error = p.touched.has(p.path) ? p.errors[p.path] : undefined
  const base = {
    id: `${p.idPrefix}-${p.path.replace(/\./g, '-')}`,
    name: p.node.name,
    label: p.node.label,
    value: typeof p.value === 'string' ? p.value : '',
    onChange: (next: string) => {
      p.markTouched(p.path)
      p.onChange(next)
    },
    onBlur: () => p.markTouched(p.path),
    required: p.node.required,
  }
  return error === undefined ? base : { ...base, error }
}

// P-037.31 fourth post-Phase-Close §B — TextField + UrlField widened
// their `onChange` signature to `(next: string | undefined) => void` so
// optional-string fields (`z.string().min(1).optional()`,
// `z.string().url().optional()`) can clear cleanly: empty input → undefined
// → writeField merges `{ ...obj, [name]: undefined }` → Zod `.optional()`
// accepts. The field components themselves do the `'' → undefined`
// coercion in their `onInput` handlers; the override here matches the
// widened prop signature so TS sees the matching call site. Same boundary-
// coercion pattern as renderImageUrl + renderSelect.
const renderText: RendererFn<'text'> = (p) => {
  const props = leafProps(p)
  return (
    <TextField
      {...props}
      onChange={(next) => {
        p.markTouched(p.path)
        p.onChange(next)
      }}
      {...(p.node.minLength !== undefined ? { minLength: p.node.minLength } : {})}
      {...(p.node.maxLength !== undefined ? { maxLength: p.node.maxLength } : {})}
    />
  )
}

const renderUrl: RendererFn<'url'> = (p) => {
  const props = leafProps(p)
  return (
    <UrlField
      {...props}
      onChange={(next) => {
        p.markTouched(p.path)
        p.onChange(next)
      }}
    />
  )
}

// RichTextField has a different value lifecycle than the input-driven leaves
// (Tiptap editor instance + paste handler + link UX + lifecycle effects), so
// it doesn't go through `leafProps`. Touched-tracking still flows through
// `markTouched(path)` on blur.
const renderRichText: RendererFn<'richText'> = (p) => {
  const error = p.touched.has(p.path) ? p.errors[p.path] : undefined
  return (
    <LazyRichTextField
      id={`${p.idPrefix}-${p.path.replace(/\./g, '-')}`}
      name={p.node.name}
      label={p.node.label}
      value={typeof p.value === 'string' ? p.value : ''}
      onChange={(next) => {
        p.markTouched(p.path)
        p.onChange(next)
      }}
      onBlur={() => p.markTouched(p.path)}
      required={p.node.required}
      {...(error !== undefined ? { error } : {})}
    />
  )
}

// P-037.31 §A — imageUrl override fires `undefined` (not `''`) on
// Clear so the form's writeField merge produces `{...obj, [name]:
// undefined}` — Zod `.optional()` accepts it; `''` would fail the
// `.url()` validation and surface "Invalid url". Same boundary-
// coercion pattern as `renderSelect` (which maps optional-select '' →
// undefined for the same Zod-rejection reason).
const renderImageUrl: RendererFn<'imageUrl'> = (p) => {
  const key = p.blockId !== undefined ? `${p.blockId}.${p.path}` : p.path
  const target = getImageTarget(key)
  // P-037.30 seventh post-Phase-Close §C — image-type label override.
  // When the registry pins a per-type human label (all 11 known fields
  // do; only the DEFAULT_TARGET fallback lacks one), use it for the
  // visible form field label. Falls through to the FieldNode's
  // humanized label (`humanize(name).replace(/\s+Url$/, '')` per
  // schema-to-fields.ts) when target.label is absent.
  const baseProps = leafProps(p)
  const props = target.label !== undefined ? { ...baseProps, label: target.label } : baseProps
  return (
    <ImageUrlField
      {...props}
      onChange={(next) => {
        p.markTouched(p.path)
        p.onChange(next)
      }}
      target={target}
      {...(p.blockId !== undefined ? { blockId: p.blockId } : {})}
    />
  )
}

// P-037.22 D-130 follow-up: the optional-enum `(none)` placeholder
// dispatches `''` from SelectField. The Zod `.enum([...]).optional()`
// schema rejects `''` (not in the enum, not undefined), so map `''`
// back to `undefined` at the dispatch boundary — parent renderers
// spread `{ ...obj, [name]: undefined }`, which `optional()` accepts
// and `JSON.stringify` omits cleanly on persistence. Required enums
// never see `''` (their placeholder is disabled), so the
// standard `leafProps` path applies.
const renderSelect: RendererFn<'select'> = (p) => {
  const props = leafProps(p)
  if (p.node.required) {
    return <SelectField {...props} options={p.node.options} />
  }
  return (
    <SelectField
      {...props}
      onChange={(next) => {
        p.markTouched(p.path)
        p.onChange(next === '' ? undefined : next)
      }}
      options={p.node.options}
    />
  )
}

const renderLiteral: RendererFn<'literal'> = ({ node }) => (
  <div class="flex flex-col gap-1">
    <span class="text-sm font-medium text-gray-800">{node.label}</span>
    <span class="text-sm text-gray-700">{String(node.value)}</span>
  </div>
)

const renderUnion: RendererFn<'union'> = ({
  node,
  value,
  path,
  idPrefix,
  errors,
  touched,
  markTouched,
  onChange,
  arrayOverrides,
  blockId,
}) => {
  const lastVariant = node.variants[node.variants.length - 1]
  if (lastVariant === undefined || lastVariant.kind !== 'object') {
    return <FieldPlaceholder kind="union" label={node.label} />
  }
  const obj =
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {}
  return (
    <div class="flex flex-col gap-3">
      {lastVariant.children.map((child) => (
        <FieldRenderer
          key={child.name}
          node={child}
          value={obj[child.name]}
          path={`${path}.${child.name}`}
          idPrefix={idPrefix}
          errors={errors}
          touched={touched}
          markTouched={markTouched}
          onChange={(next) => onChange({ ...obj, [child.name]: next })}
          {...(arrayOverrides !== undefined ? { arrayOverrides } : {})}
          {...(blockId !== undefined ? { blockId } : {})}
        />
      ))}
    </div>
  )
}

// `hidden` (P-037.24 D-132 — derived brand) renders nothing. The value
// flows through state via the standard onChange chain; only the visible
// input is suppressed. Application code generates the value (e.g., the
// composite-on-change orchestrator for `big3-signature-events`).
const renderHidden: RendererFn<'hidden'> = () => null

const FIELD_RENDERERS: FieldRenderers = {
  text: renderText,
  url: renderUrl,
  imageUrl: renderImageUrl,
  hidden: renderHidden,
  select: renderSelect,
  literal: renderLiteral,
  array: (p) => <ListField {...p} />,
  object: (p) => <NestedField {...p} />,
  union: renderUnion,
  richText: renderRichText,
  unsupported: ({ node }) => <FieldPlaceholder kind={node.kind} label={node.label} />,
}

function FieldPlaceholder({ kind, label }: { kind: FieldNode['kind']; label: string }) {
  return (
    <div class="rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
      <div class="font-medium text-gray-800">{label}</div>
      <div class="mt-1 text-xs">
        Field type <code class="font-mono">{kind}</code> not yet supported in this build.
      </div>
    </div>
  )
}
